import { CookieSetOptions } from 'universal-cookie'

type CookieInformation = {
  key: string
  options: CookieSetOptions
}

// Only use `.wearfigs.com` on the core deployments. Not for local/pr specific things that may have different domains.
const sharedDomain = ['prod', 'preview', 'staging', 'sandbox'].includes(
  process.env.NEXT_PUBLIC_FIGS_ENV ?? ''
)
  ? '.wearfigs.com'
  : undefined

const secure = process.env.NEXT_PUBLIC_FIGS_ENV !== 'local'

// These cookies should only be set/managed by CloudFront functions.
const cloudfrontManagedCookies = {
  sessionId: {
    key: 'figs-session-id',
    options: {
      domain: sharedDomain, // Set domain to sharedDomain to expose a cookie to checkout's subdomain (example: shop.wearfigs.com)
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 1800, // 30 minutes
    },
  },
  visitorId: {
    key: 'figs-visitor-id',
    options: {
      domain: sharedDomain, // Set domain to sharedDomain to expose a cookie to checkout's subdomain (example: shop.wearfigs.com)
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 34164000, // 13 months (Chrome caps at 400 days)
    },
  },
  initialSessionIndicator: {
    key: 'figs-initial-session',
    options: {
      domain: sharedDomain, // Set domain to sharedDomain to expose a cookie to checkout's subdomain (example: shop.wearfigs.com)
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 1800, // 30 minutes
    },
  },
  sessionSource: {
    key: 'figs-session-source',
    options: {
      domain: sharedDomain, // Set domain to sharedDomain to expose a cookie to checkout's subdomain (example: shop.wearfigs.com)
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 1800, // 30 minutes
    },
  },
} as const satisfies Record<PropertyKey, CookieInformation>

// These cookies should only be set/managed by Auth API response headers.
const apiManagedCookies = {
  authToken: {
    key: 'figs-auth-token',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 15600000, // .5 years
    },
  },
  authRefreshToken: {
    key: 'figs-refresh-token',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 15600000, // .5 years
    },
  },
} as const satisfies Record<PropertyKey, CookieInformation>

// Cookies controlled and owned by Magnolia
const magnoliaManagedCookies = {
  discountCode: {
    key: 'figs-discount-code',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 2419200, // 4 weeks
    },
  },
  // Non-prod feature flag overrides for testing, see FeatureFlagOverrides.server.ts.
  featureFlagOverrides: {
    key: 'figs-ff-overrides',
    options: {
      path: '/',
      secure,
      httpOnly: true,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  ssoAuthToken: {
    key: 'figs-sso-access-token',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  ssoAuthRefreshToken: {
    key: 'figs-sso-refresh-token',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  ssoShopifyCustomerToken: {
    key: 'figs-sso-customer-access-token',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  stylePreference: {
    key: 'navGender',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  promoBarDismissed: {
    key: 'figs-promo-bar-dismissed',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  contentMode: {
    key: 'figs-content',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  productData: {
    key: 'figs-product-data',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  region: {
    key: 'x-figs-shop-region',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  locale: {
    key: 'x-figs-shop-locale',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  currency: {
    key: 'x-figs-shop-currency',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  shop: {
    key: 'x-figs-shop-name',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  cartId: {
    key: 'figs-cart-id',
    options: {
      path: '/',
      secure,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 864000, // 10 days (Cart TTL from Shopify)
    },
  },
} as const satisfies Record<PropertyKey, CookieInformation>

// Cookies controlled by 3rd parties
const externalCookieKeys = {
  facebook: {
    key: '_fbc',
    options: {},
  },
  oneTrustConsents: {
    key: 'OptanonConsent', // OneTrust cookie that contains info about cookie consents
    options: {},
  },
} as const satisfies Record<PropertyKey, CookieInformation>

export const cookieKeys = {
  ...cloudfrontManagedCookies,
  ...apiManagedCookies,
  ...magnoliaManagedCookies,
  ...externalCookieKeys,
} as const satisfies Record<PropertyKey, CookieInformation>
